/**
 * Error Boundary Component
 * Catches React errors and displays fallback UI
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    isChunkError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            isChunkError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        const isChunkError = error.name === 'ChunkLoadError' ||
            error.message?.includes('Loading chunk') ||
            error.message?.includes('Failed to fetch dynamically imported module');
        return {
            hasError: true,
            isChunkError,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error to console in development
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // ChunkLoadError means a new deployment replaced old chunk files while the
        // service worker was still serving stale JS. A hard reload fetches fresh files.
        const isChunkError = error.name === 'ChunkLoadError' ||
            error.message?.includes('Loading chunk') ||
            error.message?.includes('Failed to fetch dynamically imported module');

        if (isChunkError) {
            // Prevent infinite reload loop using a monotonically-incrementing attempt
            // counter stored in sessionStorage. After MAX_ATTEMPTS the component stops
            // reloading and renders the fallback UI asking the user to manually refresh.
            const ATTEMPT_KEY = '__chunk_error_attempts';
            const MAX_ATTEMPTS = 3;
            const attempts = parseInt(sessionStorage.getItem(ATTEMPT_KEY) || '0', 10) + 1;

            if (attempts >= MAX_ATTEMPTS) {
                // Tried too many times — stop the loop, leave hasError=true so the
                // fallback "hard refresh" UI is rendered instead of an infinite reload.
                console.warn(`ChunkLoadError persists after ${attempts} reload attempts — stopping auto-retry`);
                sessionStorage.removeItem(ATTEMPT_KEY);
                // Let componentDidCatch finish; hasError is already set via getDerivedStateFromError.
                return;
            }

            sessionStorage.setItem(ATTEMPT_KEY, String(attempts));

            if (attempts === 1) {
                console.warn('ChunkLoadError detected — reloading to get fresh build (attempt 1)');
                window.location.reload();
            } else {
                // Second+ attempt: clear all SW caches before reloading.
                console.warn(`ChunkLoadError persists — clearing SW caches before retry (attempt ${attempts})`);
                if ('caches' in window) {
                    caches.keys()
                        .then((names) => Promise.all(names.map((n) => caches.delete(n))))
                        .then(() => window.location.reload());
                } else {
                    window.location.reload();
                }
            }
            return;
        }

        // Check if it's a Firestore error - these are usually transient
        const isFirestoreError = error.message?.includes('FIRESTORE') ||
            error.message?.includes('INTERNAL ASSERTION FAILED') ||
            error.message?.includes('Unexpected state');

        if (isFirestoreError) {
            console.warn('Firestore SDK error detected - attempting auto-recovery');
            // Auto-recover from Firestore errors after a short delay
            setTimeout(() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
            }, 1500);
            return;
        }

        this.setState({
            error,
            errorInfo,
        });

        // Log to error tracking service (e.g., Sentry)
        if (process.env.NODE_ENV === 'production') {
            // TODO: Send error to tracking service
            // Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
        }
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            isChunkError: false,
            error: null,
            errorInfo: null,
        });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // For chunk errors, show a minimal loading screen while auto-reload is pending
            if (this.state.isChunkError) {
                return (
                    <Box
                        sx={{
                            minHeight: '100vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            Updating app…
                        </Typography>
                    </Box>
                );
            }

            // Custom fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            const isChunkError = false;

            return (
                <Container maxWidth="md">
                    <Box
                        sx={{
                            minHeight: '100vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 4,
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                maxWidth: 600,
                            }}
                        >
                            <ErrorOutlineIcon
                                color="error"
                                sx={{ fontSize: 64, mb: 2 }}
                            />
                            <Typography variant="h4" gutterBottom color="error">
                                {isChunkError ? 'App Update Available' : 'Oops! Something went wrong'}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                {isChunkError
                                    ? 'A new version of the app was deployed. Please do a hard refresh (Ctrl+Shift+R on Windows/Linux, Cmd+Shift+R on Mac) to load the latest version.'
                                    : 'We\'re sorry for the inconvenience. The application encountered an unexpected error.'
                                }
                            </Typography>

                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        mb: 3,
                                        textAlign: 'left',
                                        bgcolor: 'grey.100',
                                        maxHeight: 200,
                                        overflow: 'auto',
                                    }}
                                >
                                    <Typography variant="subtitle2" color="error" gutterBottom>
                                        Error Details (Development Only):
                                    </Typography>
                                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {this.state.error.toString()}
                                    </Typography>
                                    {this.state.errorInfo && (
                                        <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                                            {this.state.errorInfo.componentStack}
                                        </Typography>
                                    )}
                                </Paper>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={this.handleReset}
                                >
                                    Try Again
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={this.handleReload}
                                >
                                    Reload Page
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </Container>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
